import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const NewsIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 0-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
    <line x1="16" y1="8" x2="10" y2="8"/><line x1="16" y1="12" x2="10" y2="12"/><line x1="16" y1="16" x2="10" y2="16"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const tabs = [
  { id: 'gallery', label: 'Photo Gallery', Icon: CameraIcon },
  { id: 'press', label: 'Press Releases', Icon: NewsIcon },
  { id: 'events', label: 'Upcoming Events', Icon: CalendarIcon },
  { id: 'past-events', label: 'Past Activities', Icon: CameraIcon },
];

function Media() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab');
  
  const [activeTab, setActiveTab] = useState('gallery');
  const [gallery, setGallery] = useState([]);
  const [press, setPress] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Handle URL search parameter synchronization
  useEffect(() => {
    if (activeTabParam && tabs.some(t => t.id === activeTabParam)) {
      setActiveTab(activeTabParam);
    }
  }, [activeTabParam]);

  useEffect(() => {
    async function loadMedia() {
      setLoading(true);
      setError('');
      try {
        const [galleryRes, pressRes, eventsRes] = await Promise.all([
          api.get('/gallery'),
          api.get('/press'),
          api.get('/events'),
        ]);
        setGallery(galleryRes.data);
        setPress(pressRes.data);
        setEvents(eventsRes.data);
      } catch (err) {
        console.error('Error loading media page content:', err);
        setError('Unable to load media content. Please verify your server is running.');
      } finally {
        setLoading(false);
      }
    }
    loadMedia();
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateStr).toLocaleDateString('en-US', options);
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      {/* Page Hero */}
      <section className="page-hero" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.85), rgba(8, 50, 62, 0.85)), url(/images/hero_media.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container">
          <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Media Center</div>
          <h1>Gallery &amp; Press</h1>
          <p>Moments, milestones, and announcements reflecting Mega Health Group's journey in serving our community.</p>
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="sep">›</span>
            <span className="current">Media Center</span>
          </div>
        </div>
      </section>

      {/* Media Content Section */}
      <section className="section-pad">
        <div className="container">
          {/* Tab Selector */}
          <div className="tab-bar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <tab.Icon />
                {tab.label}
              </button>
            ))}
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--teal-primary)" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </path>
              </svg>
              <p style={{ marginTop: 16, fontSize: '1rem', color: 'var(--muted)', fontWeight: 500 }}>Loading media content...</p>
            </div>
          )}
          {error && <p style={{ textAlign: 'center', color: 'var(--danger)', padding: '20px 0', fontWeight: 'bold' }}>{error}</p>}

          {/* Gallery View */}
          {!loading && !error && activeTab === 'gallery' && (
            <div>
              <div className="section-header center" style={{ marginBottom: 36 }}>
                <div className="eyebrow">Our Moments</div>
                <h2>Photo Gallery</h2>
                <p>A visual log of our clinical facility, advanced medical wings, and community service activities.</p>
              </div>
              
              {gallery.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>No gallery items found.</p>
              ) : (
                <div className="gallery-masonry">
                  {gallery.map(item => (
                    <div key={item._id} className="gallery-card">
                      <div className="gallery-thumb">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.title} />
                        ) : (
                          <span style={{ fontSize: '4.5rem' }}>{item.emoji || '📸'}</span>
                        )}
                      </div>
                      <div className="gallery-card-body">
                        <div className="date">{item.category || 'Facility'}</div>
                        <h4 style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{item.title}</h4>
                        {item.createdAt && (
                          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                            Published: {formatDate(item.createdAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Press Releases View */}
          {!loading && !error && activeTab === 'press' && (
            <div>
              <div className="section-header center" style={{ marginBottom: 36 }}>
                <div className="eyebrow">In the News</div>
                <h2>Press Releases</h2>
                <p>Official announcements, certifications, milestones, and reports from Mega Health Group.</p>
              </div>

              {press.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>No press releases found.</p>
              ) : (
                <div className="press-grid">
                  {press.map(item => (
                    <div key={item._id} className="press-card">
                      <div className="press-img">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.title} />
                        ) : (
                          <span style={{ fontSize: '4.5rem' }}>{item.emoji || '📰'}</span>
                        )}
                      </div>
                      <div className="press-body">
                        <div className="date">📅 {formatDate(item.date)}</div>
                        <h4 style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{item.title}</h4>
                        <p style={{ color: 'var(--muted)', fontSize: '0.92rem', lineHeight: 1.6 }}>{item.summary}</p>
                        {item.content && (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: '0.88rem', color: 'var(--dark)' }}>
                            {item.content}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upcoming Events View */}
          {!loading && !error && activeTab === 'events' && (
            <div>
              <div className="section-header center" style={{ marginBottom: 36 }}>
                <div className="eyebrow">Events Calendar</div>
                <h2>Upcoming Activities</h2>
                <p>Register and participate in our upcoming community screening camps, vaccination drives, and healthcare seminars.</p>
              </div>

              {events.filter(e => e.type !== 'past').length === 0 ? (
                <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>No upcoming events found.</p>
              ) : (
                <div className="events-grid">
                  {events.filter(e => e.type !== 'past').map(event => (
                    <div key={event._id} className="event-card">
                      <div className="event-img">
                        {event.imageUrl ? (
                          <img src={event.imageUrl} alt={event.title} />
                        ) : (
                          <span>{event.emoji || '📅'}</span>
                        )}
                        <div className="event-date-badge">{formatDate(event.date)}</div>
                      </div>
                      <div className="event-body">
                        <div className="event-meta">
                          <span>📍 {event.location || 'Mega Health Group'}</span>
                        </div>
                        <h4 style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{event.title}</h4>
                        <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
                          {event.description}
                        </p>
                        <Link to="/contact" className="event-link">Register Free →</Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Past Activities View */}
          {!loading && !error && activeTab === 'past-events' && (
            <div>
              <div className="section-header center" style={{ marginBottom: 36 }}>
                <div className="eyebrow">Our Legacy</div>
                <h2>Past Activities</h2>
                <p>Explore the impact and memories of our previously conducted community camps and medical seminars.</p>
              </div>

              {events.filter(e => e.type === 'past').length === 0 ? (
                <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>No past activities found.</p>
              ) : (
                <div className="events-grid">
                  {events.filter(e => e.type === 'past').map(event => (
                    <div key={event._id} className="event-card" style={{ opacity: 0.85 }}>
                      <div className="event-img">
                        {event.imageUrl ? (
                          <img src={event.imageUrl} alt={event.title} />
                        ) : (
                          <span>{event.emoji || '📅'}</span>
                        )}
                        <div className="event-date-badge">{formatDate(event.date)}</div>
                      </div>
                      <div className="event-body">
                        <div className="event-meta">
                          <span>📍 {event.location || 'Mega Health Group'}</span>
                        </div>
                        <h4 style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{event.title}</h4>
                        <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
                          {event.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Media;
